import type { CSSProperties } from 'react'
import type { NavGroup, NavItem } from '../nav/navModel'
import { OverlayScrollArea } from './OverlayScrollbar'

/**
 * SideMenu — the app chrome's vertical half (David's spec: a left hamburger side menu).
 *
 * ★ It shares the `--sidebar` token with the topbar ON PURPOSE. The two are ONE surface that
 * wraps the page in an L; they are never themed apart. See tokens.css.
 *
 * Collapsing animates WIDTH rather than unmounting, so the page area reflows smoothly and the
 * scroll position of a long menu survives a collapse/expand.
 */

const WIDTH = 232
const COLLAPSED = 0

interface Props {
  groups: NavGroup[]
  /** The id of the item currently open. */
  activeId: string | null
  open: boolean
  onNavigate: (item: NavItem) => void
  /** The git build stamp — pinned at the foot, as in the viewer's hamburger menu. */
  buildStamp: string
}

const aside: CSSProperties = {
  background: 'var(--sidebar)',
  color: 'var(--sidebar-foreground)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  transition: 'width 160ms ease, border-right-width 160ms ease',
  flex: '0 0 auto',
  borderRight: '1px solid var(--border)',
}

/**
 * The collapsed menu takes NO border. A border is not part of `width`, so a collapsed menu with
 * one still paints a 1px hairline down the page for no reason — the divider between a menu and
 * the page has nothing to divide once the menu is gone.
 */
function asideStyle(open: boolean): CSSProperties {
  return { ...aside, width: open ? WIDTH : COLLAPSED, borderRightWidth: open ? 1 : 0 }
}

const groupTitle: CSSProperties = {
  // 14px LITERAL, deliberately off the ramp (David, 2026-07-29). It was --fs-sm, not --fs-xs
  // (David, 2026-07-22): the group titles are the menu's only structural headings, and at xs
  // the uppercase + letter-spacing made them read as a caption rather than as the label the
  // options sit under. But an alias is not a decision — when --fs-sm went 14->13px the heading
  // followed it and that ruling was silently undone. Pinned here so the next scale move cannot
  // move it. ⚠ Do not "restore" it to a token: no token is 14px, and that is the point.
  //
  // ⚠ Still SMALLER than the 15px options beneath it (--fs-nav-item -> --fs-base). The
  // hierarchy is carried by uppercase + weight 600 + the green, not by size — David confirmed
  // that on 2026-07-29 when told; if headings must outrank the options, 14 is the wrong number.
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  // Dark green (Primary:Dark #355f47, ~7:1 on the near-white sidebar) — David's ask.
  color: 'var(--primary)',
  // ★ The heading's own 1px underline (David, 2026-07-28 for admin; brought across to the
  // dashboard 2026-07-29 when David spotted the two menus disagreeing).
  //
  // It is inset to the OPTION GUTTER (8px) rather than bleeding to the menu edges: every other
  // element in this menu respects that gutter, and a full-bleed rule would be the only thing in
  // the sidebar touching the chrome — which reads as a divider between sections rather than as
  // part of the heading. The text stays exactly where it was, at 16px from the edge: the 16px
  // left padding is now split 8px margin + 8px padding.
  margin: '0 8px 4px',
  padding: '16px 8px 6px',
  borderBottom: '1px solid var(--nav-group-rule)',
}

function itemStyle(active: boolean, ready: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: 'calc(100% - 16px)',
    margin: '1px 8px',
    // Left padding is 8px more than the vertical/right (7/8) so the option text sits clearly
    // indented under its group title (title text at 16px → option text at 24px). The hover/
    // selection pill still starts at 8px, so the indent is text-only — the pill keeps its
    // alignment with the group title's left edge.
    padding: '7px 8px 7px 16px',
    borderRadius: 'var(--radius)',
    border: 'none',
    textAlign: 'left',
    fontSize: 'var(--fs-nav-item)',
    fontFamily: 'var(--ff-nav-item)',
    // No `background` AT ALL: selection, hover and the resting fill are the shared
    // `.admin-option` contract in index.css. An inline background here — even `transparent` —
    // would outrank it and kill the hover tint.
    color: ready ? 'var(--foreground)' : 'var(--muted-foreground)',
    fontWeight: active ? 600 : 400,
    cursor: ready ? 'pointer' : 'default',
    opacity: ready ? 1 : 0.55,
  }
}

const phaseTag: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.04em',
  padding: '1px 5px',
  borderRadius: 999,
  border: '1px solid var(--border)',
  color: 'var(--muted-foreground)',
  fontFamily: 'var(--font-number)',
  flex: '0 0 auto',
}

/** The stamp is a build identifier — mostly digits, so it reads in the Number font. */
const stampStyle: CSSProperties = {
  padding: '10px 16px',
  borderTop: '1px solid var(--border)',
  fontSize: 11,
  fontFamily: 'var(--font-number)',
  color: 'var(--muted-foreground)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: '0 0 auto',
}

export function SideMenu({ groups, activeId, open, onNavigate, buildStamp }: Props) {
  return (
    <aside style={asideStyle(open)} aria-hidden={!open}>
      <div style={{ flex: '1 1 auto', minHeight: 0, width: WIDTH }}>
        <OverlayScrollArea axis="y">
          <nav>
            {groups.map((group) => (
              <div key={group.title}>
                <div style={groupTitle}>{group.title}</div>
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    // Selection / hover / resting fill are all the `.admin-option` contract in
                    // index.css — never an onMouseEnter state. `admin-nav-item` is kept purely
                    // as a stable hook for the E2E spec.
                    data-active={item.id === activeId ? 'true' : undefined}
                    data-disabled={item.ready ? undefined : 'true'}
                    className="admin-nav-item admin-option"
                    style={itemStyle(item.id === activeId, item.ready)}
                    onClick={() => item.ready && onNavigate(item)}
                    disabled={!item.ready}
                    title={item.ready ? undefined : `Not built yet — phase ${item.phase}`}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                    {!item.ready && <span style={phaseTag}>{item.phase}</span>}
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </OverlayScrollArea>
      </div>
      <div style={{ ...stampStyle, width: WIDTH }} title="The commit this build was made from">
        {buildStamp}
      </div>
    </aside>
  )
}
